import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { X, Clock, ArrowsCounterClockwise, CircleNotch, FloppyDisk, Trash, Sun, Prohibit } from "@phosphor-icons/react";

const TZ = "Europe/Madrid";

function isoToMadridDateTimeLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  // Get YYYY-MM-DDTHH:MM in Europe/Madrid (for <input type="datetime-local">)
  const parts = new Intl.DateTimeFormat("sv-SE", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const get = (t) => parts.find((p) => p.type === t)?.value;
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
}

function madridDateTimeLocalToISO(local) {
  // Convert "YYYY-MM-DDTHH:MM" in Madrid to a UTC ISO string
  if (!local) return null;
  // Approximate: build a date as if in Madrid by adjusting for offset
  // Use Intl to find UTC offset for that local time
  const [date, time] = local.split("T");
  const [Y, M, D] = date.split("-").map(Number);
  const [h, m] = time.split(":").map(Number);
  // Try UTC offset for Europe/Madrid at that date
  const probe = new Date(Date.UTC(Y, M - 1, D, h, m));
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: TZ,
    timeZoneName: "shortOffset",
  });
  const tzPart = fmt.formatToParts(probe).find((p) => p.type === "timeZoneName")?.value || "GMT+1";
  const m1 = tzPart.match(/GMT([+-]\d+)(?::(\d+))?/);
  const offsetH = m1 ? parseInt(m1[1]) : 1;
  const offsetM = m1 && m1[2] ? parseInt(m1[2]) : 0;
  const totalMin = offsetH * 60 + (offsetH < 0 ? -offsetM : offsetM);
  // The Madrid time corresponds to UTC = local - offset
  const utc = new Date(Date.UTC(Y, M - 1, D, h, m) - totalMin * 60 * 1000);
  return utc.toISOString();
}

function formatTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("es-ES", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: TZ,
  });
}

export default function OverrideModal({ booking, onClose, onSaved }) {
  const [onLocal, setOnLocal] = useState("");
  const [offLocal, setOffLocal] = useState("");
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    setOnLocal(isoToMadridDateTimeLocal(booking?.light_on_at));
    setOffLocal(isoToMadridDateTimeLocal(booking?.light_off_at));
  }, [booking]);

  if (!booking) return null;

  const adjustOn = (deltaMinutes) => {
    if (!onLocal) return;
    const [date, time] = onLocal.split("T");
    const [Y, M, D] = date.split("-").map(Number);
    const [h, m] = time.split(":").map(Number);
    const d = new Date(Y, M - 1, D, h, m + deltaMinutes);
    const pad = (n) => String(n).padStart(2, "0");
    setOnLocal(
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const onIso = madridDateTimeLocalToISO(onLocal);
      const offIso = madridDateTimeLocalToISO(offLocal);
      await api.setOverride(booking.primary_booking_id, onIso, offIso);
      onSaved && (await onSaved());
      onClose();
    } catch (e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm("¿Quitar el ajuste manual y volver al cálculo automático?")) return;
    setRemoving(true);
    try {
      await api.removeOverride(booking.primary_booking_id);
      onSaved && (await onSaved());
      onClose();
    } catch (e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    } finally {
      setRemoving(false);
    }
  };

  const handleDisable = async () => {
    if (
      !window.confirm(
        "¿Cancelar el encendido para esta reserva?\n\nLas luces NO se encenderán automáticamente. Podrás volver a activarlas pulsando 'Quitar ajuste'."
      )
    )
      return;
    setSaving(true);
    try {
      await api.disableOverride(booking.primary_booking_id);
      onSaved && (await onSaved());
      onClose();
    } catch (e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const handleCancelBooking = async () => {
    if (
      !window.confirm(
        "¿ANULAR la reserva completa?\n\nLa reserva desaparecerá del panel y las luces se apagarán ahora si están encendidas por esta reserva. La próxima reserva de esta pista funcionará normal. Esta acción no se puede deshacer desde el panel — para volver a verla habría que reenviar el email de reserva."
      )
    )
      return;
    setSaving(true);
    try {
      await api.cancelBooking(booking.primary_booking_id);
      onSaved && (await onSaved());
      onClose();
    } catch (e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-[#121212] border border-zinc-800 rounded-t-lg sm:rounded-md w-full max-w-md p-5 sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] text-zinc-500 tracking-[0.2em] uppercase font-mono">Ajuste manual</div>
            <h2 className="font-display font-bold text-xl mt-0.5">{booking.resource_name}</h2>
            <div className="text-xs text-zinc-400 font-mono mt-0.5">
              Reserva: {formatTime(booking.start)} – {formatTime(booking.end)}
            </div>
          </div>
          <button
            data-testid="override-modal-close"
            onClick={onClose}
            className="text-zinc-500 hover:text-white p-1"
            aria-label="Cerrar"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        {booking.override_applied && !booking.override_disabled && (
          <div className="mb-4 bg-[#CCFF00]/10 border border-[#CCFF00]/30 rounded p-2 text-xs text-[#CCFF00] flex items-center gap-2">
            <Sun size={14} weight="fill" />
            Esta reserva tiene un ajuste manual activo.
          </div>
        )}

        {booking.override_disabled && (
          <div className="mb-4 bg-red-500/10 border border-red-500/30 rounded p-2 text-xs text-red-300 flex items-center gap-2">
            <Prohibit size={14} weight="fill" />
            Encendido CANCELADO para esta reserva. Pulsa &quot;Quitar ajuste&quot; para reactivar.
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-[10px] text-zinc-500 tracking-wider uppercase font-mono mb-1.5">
              Encender luces a las
            </label>
            <input
              data-testid="override-on-input"
              type="datetime-local"
              value={onLocal}
              onChange={(e) => setOnLocal(e.target.value)}
              className="w-full bg-black border border-zinc-800 rounded px-3 py-2 text-white font-mono focus:border-[#CCFF00] focus:outline-none"
            />
            <div className="flex gap-1 mt-2">
              {[-10, -5, +5, +10, +15].map((delta) => (
                <button
                  key={delta}
                  data-testid={`override-on-adjust-${delta}`}
                  onClick={() => adjustOn(delta)}
                  className="flex-1 text-xs font-mono font-bold bg-zinc-900 border border-zinc-800 rounded py-1.5 hover:bg-zinc-800 transition-colors"
                >
                  {delta > 0 ? `+${delta}` : delta} min
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[10px] text-zinc-500 tracking-wider uppercase font-mono mb-1.5">
              Apagar luces a las
            </label>
            <input
              data-testid="override-off-input"
              type="datetime-local"
              value={offLocal}
              onChange={(e) => setOffLocal(e.target.value)}
              className="w-full bg-black border border-zinc-800 rounded px-3 py-2 text-white font-mono focus:border-[#CCFF00] focus:outline-none"
            />
          </div>

          <div className="bg-black/40 border border-zinc-900 rounded p-3 text-[11px] text-zinc-400 leading-relaxed">
            <strong className="text-zinc-300">💡 Consejo:</strong> Si hace mucho sol y quieres retrasar el encendido, usa los botones <strong>+5</strong> / <strong>+10</strong> / <strong>+15</strong>. El sistema reprogramará automáticamente las luces.
          </div>

          {!booking.override_disabled && (
            <button
              data-testid="override-disable-button"
              onClick={handleDisable}
              disabled={saving || removing}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded text-xs font-bold bg-red-950/40 border border-red-900/50 text-red-300 hover:bg-red-950/60 transition-colors disabled:opacity-50"
            >
              <Prohibit size={14} weight="bold" />
              No encender la luz para esta reserva
            </button>
          )}

          <button
            data-testid="override-cancel-booking-button"
            onClick={handleCancelBooking}
            disabled={saving || removing}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded text-xs font-bold bg-red-900/60 border border-red-700 text-red-100 hover:bg-red-900/80 transition-colors disabled:opacity-50"
          >
            <Trash size={14} weight="bold" />
            Anular reserva completa
          </button>
        </div>

        <div className="flex gap-2 mt-5">
          {booking.override_applied && (
            <button
              data-testid="override-remove-button"
              onClick={handleRemove}
              disabled={removing || saving}
              className="flex items-center gap-1.5 px-3 py-2.5 rounded text-xs font-bold bg-red-950/40 border border-red-900/50 text-red-400 hover:bg-red-950/60 transition-colors disabled:opacity-50"
            >
              {removing ? <CircleNotch size={14} className="animate-spin" /> : <Trash size={14} weight="bold" />}
              Quitar ajuste
            </button>
          )}
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded text-sm font-bold bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 transition-colors"
          >
            Cancelar
          </button>
          <button
            data-testid="override-save-button"
            onClick={handleSave}
            disabled={saving || removing}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded text-sm font-bold bg-[#CCFF00] text-black hover:bg-[#B3E600] active:scale-[0.97] transition-all disabled:opacity-50"
          >
            {saving ? <CircleNotch size={14} className="animate-spin" /> : <FloppyDisk size={14} weight="bold" />}
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}
